import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const IMG = process.env.REACT_APP_ATTACHMENT_URL;

// Review queue for near-duplicate images surfaced by the nightly
// find-attachment-near-dupes job. Pick the one to keep → Merge (drops the
// others' links on that libation), or Not duplicates → dismiss (the finder
// remembers and won't re-surface the same images together).
export default function DuplicateImages() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [keep, setKeep] = useState({}); // groupId -> chosen attachmentId

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await client.service('admin/attachment-duplicate-group').find({
        query: { status: 'pending', $limit: 50, $sort: { maxDistance: 1 } },
      });
      setGroups(res.data || []);
      // default keeper = representativeId (or first attachment)
      const k = {};
      (res.data || []).forEach((g) => {
        k[g.id] = g.representativeId || (g.attachmentIds || [])[0];
      });
      setKeep(k);
    } catch (e) {
      setError('Failed to load duplicate groups.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (group, patch) => {
    setBusyId(group.id);
    try {
      await client.service('admin/attachment-duplicate-group').patch(group.id, patch);
      setGroups((cur) => (cur || []).filter((g) => g.id !== group.id));
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const merge = (g) => resolve(g, { status: 'resolved', keepId: keep[g.id] });
  const dismiss = (g) => resolve(g, { status: 'dismissed' });

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Duplicate Images' },
        ]}
        />
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-1">Duplicate Images</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Near-duplicate wine images found by perceptual hashing (catches the same
          image re-encoded/resized across sources, which exact-match dedup misses).
          Pick the one to <strong>keep</strong> and merge, or mark <strong>not
          duplicates</strong> — dismissed pairs won&apos;t be surfaced again.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && groups === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && groups && groups.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          No duplicate groups to review. The nightly finder will add new ones here.
        </div>
      )}

      {!error && groups && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.id} className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-4">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    {g.listingId ? (
                      <NavLink to={`/admin/listings/${g.listingId}`} className="text-indigo-700 hover:underline">
                        {g.libationName || 'wine'}
                      </NavLink>
                    ) : (g.libationName || 'wine')}
                  </h3>
                  <div className="mt-1 text-xs text-gray-500">
                    {(g.attachments || []).length} similar images · distance {g.maxDistance ?? '—'}/64
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => dismiss(g)}
                    disabled={busyId === g.id}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title="These are different images — never group them again."
                  >
                    Not duplicates
                  </button>
                  <button
                    type="button"
                    onClick={() => merge(g)}
                    disabled={busyId === g.id || !keep[g.id]}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                  >
                    {busyId === g.id ? 'Working…' : 'Merge — keep selected'}
                  </button>
                </div>
              </div>

              <div className="px-4 py-3 flex flex-wrap gap-4">
                {(g.attachments || []).map((a) => {
                  const selected = keep[g.id] === a.id;
                  return (
                    <label
                      key={a.id}
                      className={`block cursor-pointer rounded-lg border-2 p-1 ${selected ? 'border-primary' : 'border-transparent'}`}
                    >
                      <img
                        src={`${IMG}/${a.path}?w=180&h=180&f=inside`}
                        alt=""
                        className="w-40 h-40 object-contain bg-gray-50 rounded"
                      />
                      <div className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="radio"
                          name={`keep-${g.id}`}
                          checked={selected}
                          onChange={() => setKeep((k) => ({ ...k, [g.id]: a.id }))}
                        />
                        {selected ? 'Keep this' : 'Keep'}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
